import {  HttpClient, HttpContext, HttpHeaders, HttpXhrBackend } from '@angular/common/http';
import { APP_INITIALIZER, EnvironmentProviders, Injectable, Provider, ENVIRONMENT_INITIALIZER, inject, InjectionToken } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AppSettings } from '../models/environment.dto';
import { environment as env } from '../../environments/environment';
import { BYPASS_LOG } from 'libs/auth/src/lib/auth.interceptor';
import {configureDevExtremeDefaults} from "../devextreme-config/configureDevExtremeDefaults";
import { DkGlobalUserSettingsService } from './dk-global-user-settings.service';

/**
 * loadAppSettings()'in `/assets/app.settings.json`'ı hangi origin'den çekeceğini belirler.
 * Sağlanmazsa (web app'lerin hepsinde olduğu gibi) davranış eskisiyle birebir aynıdır:
 * göreli path, o an sayfanın çalıştığı domain'e gider. Sadece native (Capacitor) app'ler
 * gibi kendi origin'i olmayan ortamlarda, o app'in kendi app.config.ts'i bu token'ı
 * sağlayarak müşteriye özel bir sunucu adresi enjekte edebilir.
 *
 * Dönüş değeri `null` ise: "henüz yapılandırılmadı" anlamına gelir — loadAppSettings()
 * bu durumda HTTP isteği atmadan, hata fırlatmadan sessizce boş settings ile döner
 * (bkz. mobile-kamu'daki sunucu-ayarlari ilk-kurulum akışı).
 */
export const APP_SETTINGS_BASE_URL_PROVIDER = new InjectionToken<() => Promise<string | null>>(
  'AppSettingsBaseUrlProvider'
);

@Injectable({
  providedIn: 'root'
})
export class AppSettingsService {

  appSettings!:AppSettings;

  // Her app'in main.ts'i bootstrap'tan önce `new AppSettingsService()` ile DI dışında da bir
  // instance oluşturuyor (erken app.settings.json ön-yüklemesi için) — inject() orada injection
  // context olmadığı için (NG0203) fırlatır; try/catch ile o kullanım eskisi gibi (göreli path,
  // baseUrlProvider yok) çalışmaya devam eder. Sadece Angular DI'ın oluşturduğu asıl singleton'da
  // (component/guard'lara enjekte edilen) gerçek bir sağlayıcı varsa devreye girer.
  private baseUrlProvider = (() => {
    try {
      return inject(APP_SETTINGS_BASE_URL_PROVIDER, { optional: true });
    } catch {
      return undefined;
    }
  })();

  get idleTimeoutTime(): number {
    return this.appSettings?.identityConfig?.idleTimeout ?? 60;
  }
  private settingsSubject = new BehaviorSubject<AppSettings | null>(null);
  public settings$ = this.settingsSubject.asObservable();
   httpClient:HttpClient  = new HttpClient(new HttpXhrBackend({
    build: () => new XMLHttpRequest()
}));



   async loadAppSettings(): Promise<AppSettings> {
    const baseUrl = this.baseUrlProvider ? await this.baseUrlProvider() : '';

    if (baseUrl === null) {
      // Native platformda henüz sunucu adresi girilmemiş: bootstrap'ı çökertme,
      // boş settings ile dön ki uygulama sunucu-ayarlari ekranını gösterebilsin.
      this.appSettings = {} as AppSettings;
      this.settingsSubject.next(this.appSettings);
      return this.appSettings;
    }

    // Native (Capacitor) app'lerde, kullanıcı bir sunucu adresi girdiyse (baseUrl dolu) statik
    // /assets/app.settings.json yerine backend'in CORS'a açık /Mobil/GetSetting endpoint'i
    // kullanılıyor (bkz. DaBNet.Kamu.Services'teki MobilPublicServices — [Authenticate] yok,
    // login öncesi çağrılabiliyor). Web app'lerde ve mobile-kamu'nun web/PWA sürümünde
    // (baseUrl her zaman boş string) davranış eskisiyle birebir aynı: göreli path.
    const appSettingsJson = baseUrl
      ? `${baseUrl}/Mobil/GetSetting`
      : `/assets/${env.appSettingsFile}`;
     try {
      const httpOptions = {
        headers: new HttpHeaders({
          'Accept': 'application/json'
        })
      };
       const result = await firstValueFrom(this.httpClient.get<AppSettings>(appSettingsJson,{ context: new HttpContext().set(BYPASS_LOG, true),...httpOptions }));
      this.appSettings = result;
      Object.assign(environment, result);
      this.settingsSubject.next(this.appSettings);

      return this.appSettings;
    } catch (err) {
       throw new Error(`App settings dosyası indirilemedi: ${JSON.stringify(err)}`);
    }
  }
getCompanyName(): string {
  return this.appSettings?.companyName;
}

}

export let environment:AppSettings={};

export function initializeApp(appSettingsService: AppSettingsService,globalSettings:DkGlobalUserSettingsService): () => Promise<any> {
  configureDevExtremeDefaults(globalSettings);
  return () => appSettingsService.loadAppSettings();
}

export const APP_SETTINGS = new InjectionToken<any>('AppSettings');
export const provideAppSettings = (): Array<Provider | EnvironmentProviders> => {
  return [
    DkGlobalUserSettingsService,
    AppSettingsService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppSettingsService,DkGlobalUserSettingsService],
      multi: true
    },
    {
      provide: APP_SETTINGS,
      useFactory: (appSettingsService: AppSettingsService) => {
        if (!appSettingsService.appSettings) {
          appSettingsService.loadAppSettings();
        }
        return appSettingsService.appSettings;
      },
      deps: [AppSettingsService],
    },
  ];
};

